import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ArtisanBottomNav from "@/components/ArtisanBottomNav";
import ClientBottomNav from "@/components/ClientBottomNav";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Info, User, Search, PlusCircle, MessageCircle, CheckSquare,
  UserPlus, FileText, Image as ImageIcon, Lightbulb, CalendarPlus, Images,
} from "lucide-react";

// ── Types & config ──────────────────────────────────────────────────────────

type ArticleCategory = "account" | "client" | "artisan";

const ARTICLE_CONFIG: { id: string; icon: React.ElementType; category: ArticleCategory; screenshot: string }[] = [
  { id: "platform_overview",     icon: Info,          category: "account",  screenshot: "/help/platform_overview.png"      },
  { id: "edit_profile",          icon: User,          category: "account",  screenshot: "/help/edit_profile.png"           },
  { id: "search_artisan",        icon: Search,        category: "client",   screenshot: "/help/search_artisan.png"         },
  { id: "post_request",          icon: PlusCircle,    category: "client",   screenshot: "/help/post_request.png"           },
  { id: "send_message",          icon: MessageCircle, category: "client",   screenshot: "/help/send_message.png"           },
  { id: "view_quote",            icon: CheckSquare,   category: "client",   screenshot: "/help/view_quote.png"             },
  { id: "create_artisan_account",icon: UserPlus,      category: "artisan",  screenshot: "/help/create_artisan_account.png" },
  { id: "create_quote",          icon: FileText,      category: "artisan",  screenshot: "/help/create_quote.png"           },
  { id: "create_rdv",            icon: CalendarPlus,  category: "artisan",  screenshot: "/help/create_rdv.png"             },
  { id: "create_portfolio",      icon: Images,        category: "artisan",  screenshot: "/help/create_portfolio.png"       },
];

const TABS = ["all", "account", "client", "artisan"] as const;
type Tab = typeof TABS[number];

const iconBg: Record<ArticleCategory, string> = {
  account: "bg-blue-100 text-blue-600",
  client:  "bg-accent/20 text-accent",
  artisan: "bg-primary/10 text-primary",
};

const cardBg: Record<ArticleCategory, string> = {
  account: "bg-blue-50/40 border-blue-100",
  client:  "bg-accent/5 border-accent/20",
  artisan: "bg-primary/5 border-primary/10",
};

const badgeStyle: Record<ArticleCategory, string> = {
  account: "bg-blue-50 text-blue-700 border border-blue-200",
  client:  "bg-emerald-50 text-emerald-700 border border-emerald-200",
  artisan: "bg-orange-50 text-orange-700 border border-orange-200",
};

const stepBullet: Record<ArticleCategory, string> = {
  account: "bg-blue-100 text-blue-700",
  client:  "bg-accent/20 text-accent",
  artisan: "bg-primary/10 text-primary",
};

// ── Screenshot ───────────────────────────────────────────────────────────────

const HelpScreenshot = ({ src, placeholderText }: { src?: string; placeholderText: string }) => {
  if (src) {
    return (
      <div className="mt-5 rounded-xl overflow-hidden border border-border/60 shadow-sm">
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="w-full h-auto object-cover object-top"
          loading="lazy"
          draggable={false}
        />
      </div>
    );
  }
  return (
    <div className="mt-5 rounded-xl border-2 border-dashed border-muted-foreground/20 bg-muted/20 flex flex-col items-center justify-center py-8 px-4 gap-2">
      <ImageIcon className="h-8 w-8 text-muted-foreground/30" />
      <p className="text-xs text-muted-foreground">{placeholderText}</p>
    </div>
  );
};

// ── Article accordion ────────────────────────────────────────────────────────

const ArticleList = ({
  articles,
  t,
}: {
  articles: typeof ARTICLE_CONFIG;
  t: (key: string, opts?: Record<string, unknown>) => unknown;
}) => (
  <Accordion type="single" collapsible className="space-y-2">
    {articles.map(({ id, icon: Icon, category, screenshot }) => {
      const steps = t(`articles.${id}.steps`, { returnObjects: true }) as string[];
      const tip   = t(`articles.${id}.tip`, { defaultValue: "" }) as string;
      return (
        <AccordionItem
          key={id}
          value={id}
          className={cn("rounded-xl border overflow-hidden", cardBg[category])}
        >
          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-black/5 data-[state=open]:border-b">
            <div className="flex items-center gap-3 text-start">
              <span className={cn("rounded-lg p-2 shrink-0", iconBg[category])}>
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <p className="font-semibold text-sm leading-tight">
                  {t(`articles.${id}.title`) as string}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1 font-normal">
                  {t(`articles.${id}.summary`) as string}
                </p>
              </div>
            </div>
          </AccordionTrigger>

          <AccordionContent className="px-4 pb-5 pt-3">
            <p className="text-sm text-muted-foreground mb-5">
              {t(`articles.${id}.summary`) as string}
            </p>

            {/* Étapes numérotées */}
            <ol className="space-y-2.5">
              {(Array.isArray(steps) ? steps : []).map((step, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm">
                  <span className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold mt-0.5",
                    stepBullet[category]
                  )}>
                    {idx + 1}
                  </span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>

            {/* Capture d'écran */}
            <HelpScreenshot
              src={screenshot}
              placeholderText={t("screenshot_placeholder") as string}
            />

            {/* Astuce */}
            {tip && (
              <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                <Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 leading-relaxed">{tip}</p>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      );
    })}
  </Accordion>
);

// ── Page principale ──────────────────────────────────────────────────────────

const Aide = () => {
  const { t, locale } = useLanguage("aide");
  const lp = useLocalizedPath();
  const [activeTab, setActiveTab] = useState<Tab>("all");

  useSEO({
    title:       t("page_title") as string,
    description: t("meta_desc")  as string,
    canonical:   lp("/aide"),
    locale:      locale as "fr" | "ar",
  });

  const filteredArticles = activeTab === "all"
    ? ARTICLE_CONFIG
    : ARTICLE_CONFIG.filter((a) => a.category === activeTab);

  const groupedCategories = (["account", "client", "artisan"] as ArticleCategory[]).map(
    (cat) => ({ cat, articles: ARTICLE_CONFIG.filter((a) => a.category === cat) })
  );

  return (
    <div className="min-h-screen">
      <Navbar />

      <main>
        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-12 md:py-16 border-b">
          <div className="container max-w-3xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary mb-4">
              <Info className="h-3.5 w-3.5" />
              {t("badge") as string}
            </span>
            <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              {t("hero_title") as string}
            </h1>
            <p className="mt-3 text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
              {t("hero_subtitle") as string}
            </p>
          </div>
        </section>

        {/* ── Onglets catégories ─────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
          <div className="container max-w-3xl">
            <div className="flex gap-1 py-2 overflow-x-auto scrollbar-hide">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
                    activeTab === tab
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {t(`categories.${tab}`) as string}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Contenu ───────────────────────────────────────────────────── */}
        <section className="container max-w-3xl py-8 space-y-10 pb-24 md:pb-12">
          {activeTab === "all" ? (
            groupedCategories.map(({ cat, articles }) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-4">
                  <span className={cn(
                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                    badgeStyle[cat]
                  )}>
                    {t(`categories.${cat}`) as string}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <ArticleList articles={articles} t={t as (key: string, opts?: Record<string, unknown>) => unknown} />
              </div>
            ))
          ) : (
            <ArticleList articles={filteredArticles} t={t as (key: string, opts?: Record<string, unknown>) => unknown} />
          )}
        </section>
      </main>

      <Footer />
      <ArtisanBottomNav />
      <ClientBottomNav />
    </div>
  );
};

export default Aide;
